import React, { useEffect, useRef } from 'react';
import QRCode from 'qrcode';

interface CheckInQRCardProps {
  registrationId: string;
  guestName: string;
  tierName: string;
  referenceId: string;
  orderReferenceId?: string | null;
  zoneName?: string | null;
  tableName?: string | null;
  tableNumber?: number | null;
  seatNumber?: number | null;
  isGaChapter?: boolean;
}

const styles: Record<string, React.CSSProperties> = {
  card: {
    backgroundColor: '#fff',
    border: '2px solid #1a6b3c',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(26, 107, 60, 0.1)',
  },
  titleBand: {
    backgroundColor: '#0f2d1f',
    padding: '0.6rem 1rem',
  },
  titleText: {
    fontSize: '0.75rem',
    fontWeight: 700,
    color: '#fff',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    margin: 0,
  },
  body: {
    padding: '1rem 1.25rem',
  },
  nameRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '0.25rem',
  },
  guestName: {
    fontSize: '1rem',
    fontWeight: 700,
    color: '#0f2d1f',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.3px',
    margin: 0,
    flex: 1,
  },
  rightColumn: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'flex-end',
  },
  tierLabel: {
    fontSize: '0.8rem',
    fontWeight: 700,
    color: '#4a3080',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  zoneLabel: {
    fontSize: '0.7rem',
    fontWeight: 500,
    color: '#6b7280',
    marginTop: '2px',
  },
  qrSection: {
    textAlign: 'center' as const,
    margin: '1rem 0',
  },
  qrHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    marginBottom: '0.5rem',
  },
  qrTitle: {
    fontSize: '0.9rem',
    fontWeight: 700,
    color: '#1a6b3c',
    margin: 0,
  },
  badge: {
    display: 'inline-block',
    padding: '3px 8px',
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    borderRadius: '12px',
    fontSize: '0.7rem',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
  },
  qrContainer: {
    display: 'flex',
    justifyContent: 'center',
    margin: '0.5rem 0',
  },
  canvas: {
    borderRadius: '8px',
    border: '1px solid #e0e0e0',
  },
  refSection: {
    marginTop: '1rem',
    fontSize: '0.8rem',
    color: '#666',
  },
  refId: {
    fontFamily: 'monospace',
    fontWeight: 600,
    color: '#333',
  },
  seatSection: {
    marginTop: '0.75rem',
    borderTop: '1px solid #e5e7eb',
    paddingTop: '0.75rem',
  },
  seatLabel: {
    fontSize: '0.7rem',
    fontWeight: 700,
    color: '#6b7280',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    margin: '0 0 0.25rem 0',
  },
  seatValue: {
    fontSize: '0.9rem',
    fontWeight: 600,
    color: '#0f2d1f',
  },
  instruction: {
    fontSize: '0.7rem',
    color: '#1a6b3c',
    fontWeight: 500,
    marginTop: '0.75rem',
    fontStyle: 'italic',
    textAlign: 'center' as const,
  },
};

export function CheckInQRCard({
  registrationId,
  guestName,
  tierName,
  referenceId,
  orderReferenceId,
  zoneName,
  tableName,
  tableNumber,
  seatNumber,
  isGaChapter,
}: CheckInQRCardProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isPatron = tierName?.toLowerCase() === 'patron';

  useEffect(() => {
    if (canvasRef.current) {
      const payload = JSON.stringify({ type: 'guest', identifier: registrationId });
      const qrBackgroundColor = isPatron ? '#e6e0f3' : '#ffffff';
      QRCode.toCanvas(canvasRef.current, payload, {
        width: 200,
        margin: 2,
        color: { dark: '#0f2d1f', light: qrBackgroundColor },
      }).catch(() => {
        // Silently fail
      });
    }
  }, [registrationId, isPatron]);

  const cardStyle: React.CSSProperties = {
    ...styles.card,
    ...(isPatron ? { borderColor: '#7c3aed' } : {}),
  };

  return (
    <div style={cardStyle}>
      {/* Title Band */}
      <div style={{ ...styles.titleBand, ...(isPatron ? { backgroundColor: '#4a3080' } : {}) }}>
        <p style={styles.titleText}>
          REF/ORDER | {orderReferenceId || referenceId}
        </p>
      </div>

      {/* Body */}
      <div style={styles.body}>
        {/* Name + Tier/Zone Row */}
        <div style={styles.nameRow}>
          <p style={styles.guestName}>{guestName}{isGaChapter && <img src="/images/KDUGaLogo.png" alt="KDU Georgia" style={{ width: '20px', height: '20px', borderRadius: '3px', marginLeft: '6px', verticalAlign: 'middle' }} />}</p>
          <div style={styles.rightColumn}>
            <span style={styles.tierLabel}>{tierName?.toUpperCase()}</span>
            <span style={styles.zoneLabel}>ZONE: {zoneName || 'Not assigned'}</span>
          </div>
        </div>

        {/* QR Code Section - Centered */}
        <div style={styles.qrSection}>
          <div style={styles.qrHeader}>
            <span style={{ fontSize: '1.1rem' }}>🎫</span>
            <h3 style={styles.qrTitle}>Check-In QR Code</h3>
            <span style={styles.badge}>Ready</span>
          </div>
          <div style={styles.qrContainer}>
            <canvas ref={canvasRef} style={styles.canvas} />
          </div>
        </div>

        {/* Ref: Ticket# */}
        <div style={styles.refSection}>
          Ref: <span style={styles.refId}>{referenceId}</span>
        </div>

        {/* Seat Section */}
        <div style={styles.seatSection}>
          <p style={styles.seatLabel}>Seat</p>
          <p style={styles.seatValue}>
            {(tableName || tableNumber != null) && seatNumber != null
              ? `${tableName || `Table ${tableNumber}`} | Seat ${seatNumber}`
              : 'Not yet assigned'}
          </p>
        </div>

        <p style={styles.instruction}>
          Show this to the organizer at the door for quick check-in
        </p>
      </div>
    </div>
  );
}
